import { Idiomorph } from "idiomorph";

export interface MorphIframeDocumentOptions {
  /** Region ids with unsaved local edits — these islands are not morphed. */
  protectedEditIds?: ReadonlySet<string>;
  /** Whether the user is actively focused inside an editable island. */
  userIsEditing?: boolean;
}

function getEditableIslandEditId(node: Node): string | null {
  if (!(node instanceof Element)) return null;
  const island = node.closest("[data-edit-id]:not([data-no-edit])");
  if (!island) return null;
  return island.getAttribute("data-edit-id");
}

function isProtectedIslandNode(
  node: Node,
  protectedEditIds?: ReadonlySet<string>,
): boolean {
  if (!protectedEditIds || protectedEditIds.size === 0) return false;
  const editId = getEditableIslandEditId(node);
  return editId !== null && protectedEditIds.has(editId);
}

function isCohesiveInjectedNode(node: Node): boolean {
  if (!(node instanceof Element)) return false;
  return (
    node.hasAttribute("data-cohesive-nav-guard") ||
    node.hasAttribute("data-cohesive-force-light") ||
    node.hasAttribute("data-cohesive-edit") ||
    node.hasAttribute("data-cohesive-injected")
  );
}

function isNavGuardScript(node: Node): boolean {
  return (
    node instanceof HTMLScriptElement &&
    node.hasAttribute("data-cohesive-nav-guard")
  );
}

/**
 * Morph prepared iframe HTML into a live iframe document without a full
 * `srcDoc` navigation. Call after the first load when content identity changes.
 */
export function morphPreparedHtmlIntoDocument(
  liveDoc: Document,
  preparedHtml: string,
  options: MorphIframeDocumentOptions = {},
): void {
  const { protectedEditIds, userIsEditing = false } = options;
  const parsed = new DOMParser().parseFromString(preparedHtml, "text/html");
  const liveRoot = liveDoc.documentElement;
  const parsedRoot = parsed.documentElement;

  if (!liveRoot || !parsedRoot) {
    throw new Error("morphPreparedHtmlIntoDocument: missing documentElement");
  }

  const activeElement = liveDoc.activeElement;

  Idiomorph.morph(liveRoot, parsedRoot.outerHTML, {
    morphStyle: "outerHTML",
    ignoreActive: userIsEditing,
    ignoreActiveValue: userIsEditing,
    restoreFocus: !userIsEditing,
    head: { style: "merge" },
    callbacks: {
      beforeNodeMorphed(oldNode, newNode) {
        if (isNavGuardScript(oldNode) || isNavGuardScript(newNode)) {
          return false;
        }
        if (isProtectedIslandNode(oldNode, protectedEditIds)) {
          return false;
        }
        if (
          userIsEditing &&
          activeElement &&
          (oldNode === activeElement ||
            (oldNode instanceof Element &&
              activeElement instanceof Element &&
              oldNode.contains(activeElement)))
        ) {
          return false;
        }
        return true;
      },
      beforeNodeRemoved(node) {
        if (isCohesiveInjectedNode(node)) {
          return false;
        }
        if (isProtectedIslandNode(node, protectedEditIds)) {
          return false;
        }
        return true;
      },
    },
  });
}
